import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { AlertTriangle, Search } from "lucide-react";
import Checkbox from "@/components/Core/Checkbox/Checkbox";
import SearchableCheckboxList from "@/components/Core/SearchableCheckboxList";
import { Section } from "@/components/Shared/Filters/filterControls";
import {
	Dialog,
	DialogClose,
	DialogContent,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { InvoiceBatchFilter, InvoiceBatchScopeCustomerRow } from "@/models/InvoiceBatch";
import { useCustomFieldDefinitions } from "@/networking/organisation.hooks";
import { isGroupOwnedCustomField } from "@/components/Shared/CustomFields";
import { cn } from "@/utils/cn";
import { toggleFilterGroup } from "@/utils/filter.utils";
import { FilterOption } from "@/components/Shared/FilterGroup/FilterGroup";
import InvoiceBatchCustomFieldFilters from "./InvoiceBatchCustomFieldFilters";
import { setCustomersExcluded } from "./invoiceBatchFilter.utils";
import { InvoiceBatchFilterOptions } from "./useInvoiceBatchFilterOptions";

interface Props {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	filter: InvoiceBatchFilter;
	onChange: (filter: InvoiceBatchFilter) => void;
	options: InvoiceBatchFilterOptions;
	/**
	 * Rows from the scope projection: every customer the filter groups match,
	 * including blocked and manually excluded ones. Undefined until the first
	 * projection has resolved.
	 */ customers?: InvoiceBatchScopeCustomerRow[];
	isLoading?: boolean;
	/**
	 * Locked view for an existing batch: the selection is shown as it was
	 * persisted, nothing is editable. Rows are built from the batch itself
	 * (invoices + resolved exclusions) rather than the live projection.
	 */
	readOnly?: boolean;
}

/** Diacritics-insensitive match, same rationale as SearchableCheckboxList. */
const normalize = (s: string) => s.toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "");

const toListOptions = (options: FilterOption[]) =>
	options.map(({ key, name }) => ({ value: key, label: name }));

/**
 * The advanced half of the invoice batch's customer scoping: customer
 * tag / contract / custom field filters on the left, and the live
 * list of matched customers on the right where individual customers can be
 * unchecked. Strict exclude-only — a checkbox can never add a customer the
 * filter groups do not match.
 *
 * Check state derives from the local `filter.excludedCustomerIds`, not the
 * server echo, so toggling feels instant while the debounced projection
 * catches up. Blocked customers render greyed out with the reason and cannot
 * be checked — fixing the customer, not this dialog, unblocks them.
 */
const InvoiceBatchAdvancedFilterDialog: React.FC<Props> = ({
	open,
	onOpenChange,
	filter,
	onChange,
	options,
	customers,
	isLoading,
	readOnly,
}) => {
	const { t } = useTranslation();
	const allCustomerFieldDefinitions = useCustomFieldDefinitions("customer");
	// Group-owned fields are left out: the filter matches values stored on the
	// customer, and a koncern member's value lives on the koncern (the backend
	// rejects them with INVALID_CUSTOM_FIELDS).
	const customFieldDefinitions = useMemo(
		() => allCustomerFieldDefinitions.filter((definition) => !isGroupOwnedCustomField(definition)),
		[allCustomerFieldDefinitions]
	);
	const [query, setQuery] = useState("");

	const rows = customers ?? [];
	const excludedIds = new Set(filter.excludedCustomerIds ?? []);

	const selectableRows = rows.filter((row) => !row.blockedBy);
	const checkedRows = selectableRows.filter((row) => !excludedIds.has(row.id));
	const totalTimeReports = checkedRows.reduce((sum, row) => sum + row.invoiceableTimeReports, 0);

	// Only meaningful in readOnly mode, where rows carry the persisted state
	const includedRowCount = rows.filter((row) => !row.excluded).length;
	const excludedRowCount = rows.length - includedRowCount;

	const visibleRows = useMemo(() => {
		const q = normalize(query.trim());
		if (!q) return rows;
		return rows.filter((row) => normalize(row.name ?? row.id).includes(q));
	}, [rows, query]);

	// (De)select-all acts on what is on screen, so it stays predictable while a
	// search narrows the list. Blocked rows are never touched — they cannot be
	// included at all.
	const visibleSelectableRows = visibleRows.filter((row) => !row.blockedBy);
	const areAllVisibleChecked =
		visibleSelectableRows.length > 0 &&
		visibleSelectableRows.every((row) => !excludedIds.has(row.id));

	function toggleAllVisible() {
		onChange(
			setCustomersExcluded(
				filter,
				visibleSelectableRows.map((row) => row.id),
				areAllVisibleChecked
			)
		);
	}

	function toggleGroup(
		group: keyof Pick<InvoiceBatchFilter, "tagKeys" | "contractIds">,
		key: string
	) {
		onChange(toggleFilterGroup(filter, group, key));
	}

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="max-w-3xl">
				<DialogHeader>
					<DialogTitle>{t("invoiceBatch.filter.advancedSelection")}</DialogTitle>
				</DialogHeader>

				<div className="grid grid-cols-1 gap-6 md:grid-cols-2">
					<div>
						{options.tags.length > 0 && (
							<Section
								title={t("invoiceBatch.filter.customerTags")}
								count={filter.tagKeys?.length ?? 0}
								scrollable
								flush
							>
								<SearchableCheckboxList
									options={toListOptions(options.tags)}
									selected={filter.tagKeys ?? []}
									onToggle={(key) => toggleGroup("tagKeys", key)}
									searchPlaceholder={t("invoiceBatch.filter.searchTag")}
									disabled={readOnly}
								/>
							</Section>
						)}
						{options.contracts.length > 0 && (
							<Section
								title={t("CONTRACT" as any)}
								count={filter.contractIds?.length ?? 0}
								scrollable
								flush
							>
								<SearchableCheckboxList
									options={toListOptions(options.contracts)}
									selected={filter.contractIds ?? []}
									onToggle={(key) => toggleGroup("contractIds", key)}
									searchPlaceholder={t("invoiceBatch.filter.searchContract")}
									disabled={readOnly}
								/>
							</Section>
						)}{" "}
						<InvoiceBatchCustomFieldFilters
							filter={filter}
							onChange={onChange}
							definitions={customFieldDefinitions}
							disabled={readOnly}
						/>
					</div>

					<div className={cn(isLoading && "opacity-60")}>
						<div className="mb-1 text-base font-medium text-gray-800">
							{t("invoiceBatch.filter.customers")}
						</div>

						<div className="mb-2 flex items-center justify-between gap-2">
							{" "}
							<div className="text-sm text-gray-600">
								{readOnly
									? t("invoiceBatch.filter.readOnlySelection", {
											included: includedRowCount,
											excluded: excludedRowCount,
									  })
									: t("invoiceBatch.filter.selectedCustomers", {
											selected: checkedRows.length,
											total: rows.length,
											timeReports: totalTimeReports,
									  })}
							</div>{" "}
							{!readOnly && (
								<button
									type="button"
									onClick={toggleAllVisible}
									disabled={!visibleSelectableRows.length}
									className="shrink-0 text-sm text-brand hover:underline disabled:cursor-not-allowed disabled:text-gray-400 disabled:no-underline"
								>
									{t(areAllVisibleChecked ? "DESELECT_ALL" : "SELECT_ALL")}
								</button>
							)}
						</div>

						{rows.length > 8 && (
							<div className="relative mb-2">
								<Search
									size={14}
									className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400"
								/>
								<input
									value={query}
									onChange={(e) => setQuery(e.target.value)}
									placeholder={t("invoiceBatch.filter.searchCustomer")}
									className="w-full rounded-md border border-gray-300 py-1.5 pl-8 pr-2 text-sm focus:outline-none focus:ring-1 focus:ring-gray-400"
								/>
							</div>
						)}

						<div className="max-h-[45vh] divide-y divide-gray-100 overflow-auto rounded-md border border-gray-200">
							{visibleRows.length === 0 && (
								<div className="p-3 text-sm text-gray-500">
									{t("invoiceBatch.filter.noCustomers")}
								</div>
							)}

							{visibleRows.map((row) => {
								const isBlocked = !!row.blockedBy;
								const isChecked = !isBlocked && !excludedIds.has(row.id);

								return (
									<div
										key={row.id}
										className={cn("flex items-center gap-2 px-3 py-2", isBlocked && "opacity-50")}
									>
										{" "}
										<Checkbox
											customStyle
											isChecked={isChecked}
											disabled={isBlocked || readOnly}
											onChange={() =>
												!readOnly &&
												onChange(toggleFilterGroup(filter, "excludedCustomerIds", row.id))
											}
										/>
										<div className="min-w-0 flex-1">
											<div className="flex items-center gap-1.5">
												<span className="truncate text-sm text-gray-800">{row.name ?? row.id}</span>

												{row.deliveryMethod && (
													<span className="shrink-0 rounded bg-gray-100 px-1.5 py-0.5 text-[11px] text-gray-600">
														{t(`deliveryMethod.label.${row.deliveryMethod}` as any)}
													</span>
												)}

												{row.hasInvalidGln && (
													<span title={t("invoiceBatch.issueType.INVALID_GLN_CODE")}>
														<AlertTriangle size={14} className="shrink-0 text-amber-500" />
													</span>
												)}
											</div>{" "}
											{readOnly ? (
												row.excluded && (
													<div className="text-xs text-gray-500">
														{t("invoiceBatch.filter.excludedRow")}
													</div>
												)
											) : (
												<div className="text-xs text-gray-500">
													{isBlocked
														? [
																t(`invoiceBatch.issueType.${row.blockedBy}` as any),
																row.blockedBy === "DELIVERY_METHOD_INCOMPLETE" &&
																	row.missingDeliveryField &&
																	t(`deliveryMethod.field.${row.missingDeliveryField}` as any),
														  ]
																.filter(Boolean)
																.join(": ")
														: t("invoiceBatch.filter.timeReports", {
																count: row.invoiceableTimeReports,
														  })}
												</div>
											)}
										</div>
									</div>
								);
							})}
						</div>
					</div>
				</div>

				<DialogFooter>
					<DialogClose asChild>
						<button
							type="button"
							className="rounded-md bg-gray-900 px-4 py-2 text-sm text-white hover:bg-gray-800"
						>
							{t("invoiceBatch.filter.done")}
						</button>
					</DialogClose>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
};

export default InvoiceBatchAdvancedFilterDialog;
